import { useMemo } from 'react';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import type { GallerySortName } from '@sorare/core/src/contexts/config';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import type { GQLCtx } from '@sorare/core/src/contexts/graphql-search';
import { keyFacetsByApiField } from '@sorare/core/src/contexts/graphql-search';
import { BACKEND_CARD_SEARCH_FILTERS } from '@sorare/core/src/contexts/graphql-search/backendCardSearchFilters.ts';
import { pickRenderedBackendFilters } from '@sorare/core/src/contexts/graphql-search/pickRenderedBackendFilters.ts';
import { useGQLSearch } from '@sorare/core/src/contexts/graphql-search/useGQLSearch.ts';
import { useSearchCardsContext } from '@sorare/core/src/contexts/searchCards/index.tsx';
import { useCompetitionEligibilityFilterValue } from '@sorare/core/src/contexts/searchCards/useCompetitionEligibilityFilterValue.ts';
import { useFavoriteFilterValue } from '@sorare/core/src/contexts/searchCards/useFavoriteFilterValue.ts';
import { useFixtureFilterValue } from '@sorare/core/src/contexts/searchCards/useFixtureFilterValue.ts';
import { useProbablePitchersFilterValue } from '@sorare/core/src/contexts/searchCards/useProbablePitchersFilterValue.ts';
import { useWatchlistFilterValue } from '@sorare/core/src/contexts/searchCards/useWatchlistFilterValue.ts';
import { useLazyQuery } from '@sorare/core/src/hooks/graphql/useLazyQuery';
import {
  joinFiltersWithAnd,
  joinFiltersWithOr,
  sportFilter,
} from '@sorare/core/src/lib/algolia.ts';
import type { FiltersGroup } from '@sorare/core/src/lib/filters';

import { toSortInputs } from '#src/hooks/search/useCardsSearchClient.ts';
import { MARKET_CARDS_SEARCH_FACET_VALUES_QUERY } from '#src/hooks/search/useMarketCardsSearch.ts';

import type { MarketCardsSearchQuery } from './__generated__/useMarketCardsSearch.graphql.ts';

type MarketSearchResult = Pick<
  MarketCardsSearchQuery['searchCards'],
  'page' | 'nbPages' | 'nbHits' | 'facets'
>;

type Params = {
  sport: Sport;
  cardFilters: FiltersGroup[];
  defaultFilters?: string[];
  defaultSort: GallerySortName;
  onSaleOnly?: boolean;
};

const creditEligibleFilterValue = joinFiltersWithOr([
  'sale.type:PrimaryOffer',
  'sale.type:EnglishAuction',
]);

const rendersCollectibleFilter = (cardFilters: FiltersGroup[]) =>
  cardFilters.some(group =>
    [...group.filters, ...(group.filter ? [group.filter] : [])].some(
      f => 'attribute' in f && f.attribute === 'co'
    )
  );

export const useBackendMarketCardsSearch = ({
  sport,
  cardFilters,
  defaultFilters = [],
  defaultSort,
  onSaleOnly = true,
}: Params) => {
  const activeBackendCardFilters = useMemo(
    () => pickRenderedBackendFilters(BACKEND_CARD_SEARCH_FILTERS, cardFilters),
    [cardFilters]
  );

  const { ctxBase, facets, searchParams } = useGQLSearch(
    activeBackendCardFilters
  );
  const { query, page, activeFacets, refinements, setSearchParams } = ctxBase;

  const currentSortName = (searchParams.get('s') ??
    defaultSort) as GallerySortName;

  const { filters: searchCardsFilters } = useSearchCardsContext();

  const favoriteActive = !!activeFacets.ff?.length;
  const favoriteFilterValue = useFavoriteFilterValue({ skip: !favoriteActive });

  const creditEligibleActive = !!activeFacets.ce?.length;

  const competitionEligibility = activeFacets.lf?.[0];
  const competitionEligibilityFilterValue =
    useCompetitionEligibilityFilterValue(competitionEligibility);

  const gameweekSlug = activeFacets.gw?.[0];
  const gameweekFilterValue = useFixtureFilterValue({
    filterValue: gameweekSlug || undefined,
    eventType: So5FixtureEvent.CLASSIC,
  });

  const dailySlug = activeFacets.dly?.[0];
  const dailyFilterValue = useFixtureFilterValue({
    filterValue: dailySlug || undefined,
    eventType: So5FixtureEvent.DAILY,
  });

  const probablePitchersValue = activeFacets.pp?.[0];
  const probablePitchersFilterValue = useProbablePitchersFilterValue({
    probablePitchersFilterValue: probablePitchersValue,
    skip: !probablePitchersValue,
  });

  const { promotionalEvents } = useConfigContext();
  const promotionalEventSlug = activeFacets.promotion?.[0];
  const promotionalEventFilterValue = promotionalEvents.find(
    ({ slug }) => slug === promotionalEventSlug
  )?.algoliaFilters;

  const watchlistId = activeFacets.wl?.[0];
  const { watchlistFilterValue, loading: watchlistLoading } =
    useWatchlistFilterValue({ filterValue: watchlistId || undefined });

  const filtersLoading = watchlistLoading;

  const excludeCollectibles =
    rendersCollectibleFilter(cardFilters) && !activeFacets.co?.length;

  const advancedFilters = joinFiltersWithAnd(
    [
      ...defaultFilters,
      ...searchCardsFilters,
      sportFilter(sport),
      favoriteActive && favoriteFilterValue,
      creditEligibleActive && creditEligibleFilterValue,
      gameweekSlug && gameweekFilterValue,
      dailySlug && dailyFilterValue,
      probablePitchersValue && probablePitchersFilterValue,
      promotionalEventSlug && promotionalEventFilterValue,
      competitionEligibility && competitionEligibilityFilterValue,
      watchlistId && watchlistFilterValue,
      excludeCollectibles && 'NOT rarity:custom_series',
    ].filter(Boolean) as string[]
  );

  const [search] = useLazyQuery(MARKET_CARDS_SEARCH_FACET_VALUES_QUERY, {
    variables: {
      query,
      advancedFilters,
      refinements,
      field: '',
      facetQuery: '',
      onSaleOnly,
    },
  });

  const makeGqlSearchContextValue = (
    result: MarketSearchResult | undefined
  ): GQLCtx => ({
    ...ctxBase,
    search,
    extractFacetValues: searchFacetData =>
      searchFacetData?.searchCardFacetValues,
    pages: result?.nbPages ?? 0,
    nbHits: result?.nbHits ?? 0,
    advancedFilters,
    onSaleOnly,
    sorts: [currentSortName],
    facets: keyFacetsByApiField(result?.facets ?? []),
  });

  return {
    query,
    page,
    refinements,
    facets,
    activeFacets,
    advancedFilters,
    currentSortName,
    sortInputs: toSortInputs(currentSortName, sport),
    filtersLoading,
    setSearchParams,
    searchParams,
    makeGqlSearchContextValue,
  };
};
